import { useState } from "react";
import { useData } from "@/context/DataContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { CaretRight, Plus, Trash, Clock } from "@phosphor-icons/react";
import { formatTime } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { deleteWithUndo } from "@/lib/undo";

const PRIORITY_LABEL = {
  must: { title: "Moet doen", tone: "text-rose-400", bar: "bg-rose-500" },
  should: { title: "Zou moeten", tone: "text-amber-400", bar: "bg-amber-500" },
  if_time: { title: "Als er tijd is", tone: "text-slate-400", bar: "bg-slate-500" },
};

function TaskItem({ task }) {
  const { updateTask, deleteTask, restoreTask, catById } = useData();
  const [expanded, setExpanded] = useState(false);
  const [newSub, setNewSub] = useState("");
  const cat = catById(task.category_id);
  const subs = task.subtasks || [];
  const done = subs.filter((s) => s.completed).length;
  const progress = subs.length ? Math.round((done / subs.length) * 100) : task.completed ? 100 : 0;

  const toggle = () => updateTask(task.id, { completed: !task.completed });

  const toggleSub = (sid) => {
    const next = subs.map((s) => (s.id === sid ? { ...s, completed: !s.completed } : s));
    const allDone = next.length > 0 && next.every((s) => s.completed);
    updateTask(task.id, { subtasks: next, completed: allDone });
  };

  const addSub = () => {
    if (!newSub.trim()) return;
    const next = [...subs, { id: crypto.randomUUID(), title: newSub, completed: false }];
    updateTask(task.id, { subtasks: next });
    setNewSub("");
  };

  const delSub = (sid) => {
    updateTask(task.id, { subtasks: subs.filter((s) => s.id !== sid) });
  };

  return (
    <div className="group border border-slate-800 rounded-lg bg-slate-900/40 overflow-hidden">
      <div className="flex items-start gap-3 p-3">
        <button
          data-testid={`task-toggle-${task.id}`}
          onClick={toggle}
          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
            task.completed ? "bg-blue-500 border-blue-500" : "border-slate-600 hover:border-blue-400"
          }`}
        >
          {task.completed && <span className="text-[9px] text-white">✓</span>}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${task.completed ? "task-complete" : "text-slate-100"} font-medium`}>
            {task.title}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5 flex-wrap">
            {task.due_time && (
              <span className="inline-flex items-center gap-1"><Clock size={11} />{formatTime(task.due_time)}</span>
            )}
            {task.estimated_minutes && <span>~{task.estimated_minutes} min</span>}
            {cat && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />{cat.name}</span>}
            {subs.length > 0 && <span>{done}/{subs.length} subtaken</span>}
          </div>
          {subs.length > 0 && (
            <div className="mt-2">
              <Progress value={progress} className="h-1 bg-slate-800" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            data-testid={`task-expand-${task.id}`}
            onClick={() => setExpanded((v) => !v)}
            className={`p-1 rounded hover:bg-slate-800/70 text-slate-400 hover:text-slate-100 transition-transform ${expanded ? "rotate-90" : ""}`}
          >
            <CaretRight size={14} />
          </button>
          <button
            data-testid={`task-delete-${task.id}`}
            onClick={() => deleteWithUndo({
              label: `"${task.title}" deleted`,
              deleteFn: async () => { await deleteTask(task.id); },
              restoreFn: async () => { await restoreTask(task); },
            })}
            className="p-1 rounded hover:bg-slate-800/70 text-slate-400 hover:text-rose-400"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-800/70 px-3 py-2 space-y-1.5 bg-slate-900/60">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center gap-2 group/sub">
              <Checkbox
                checked={s.completed}
                onCheckedChange={() => toggleSub(s.id)}
                className="border-slate-600 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                data-testid={`subtask-toggle-${s.id}`}
              />
              <span className={`text-sm ${s.completed ? "task-complete" : "text-slate-300"}`}>{s.title}</span>
              <button
                onClick={() => delSub(s.id)}
                className="ml-auto text-slate-500 opacity-0 group-hover/sub:opacity-100 hover:text-rose-400"
              ><Trash size={12} /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSub()}
              placeholder="Subtaak toevoegen..."
              className="h-8 text-xs bg-slate-900 border-slate-800 text-slate-100"
              data-testid={`subtask-input-${task.id}`}
            />
            <Button size="sm" onClick={addSub} className="h-8 bg-blue-600 hover:bg-blue-500 gap-1 text-xs">
              <Plus size={12} /> Toevoegen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaskList({ tasks, title = "Taken van vandaag", emptyText = "Nog geen taken. Voeg er een toe via Snel toevoegen." }) {
  const grouped = { must: [], should: [], if_time: [] };
  (tasks || []).forEach((t) => grouped[t.priority || "should"]?.push(t));

  const total = tasks.length;
  const done = tasks.filter((t) => t.completed).length;
  const overallProgress = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Taken</div>
          <div className="text-slate-100 font-medium">{title}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">{done}/{total} klaar</div>
          <div className="w-28 mt-1">
            <Progress value={overallProgress} className="h-1 bg-slate-800" />
          </div>
        </div>
      </div>
      {total === 0 && (
        <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg">
          {emptyText}
        </div>
      )}
      <div className="space-y-6">
        {["must", "should", "if_time"].map((p) => {
          const items = grouped[p];
          if (items.length === 0) return null;
          const label = PRIORITY_LABEL[p];
          return (
            <div key={p}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-1.5 h-1.5 rounded-full ${label.bar}`} />
                <span className={`text-[11px] font-semibold uppercase tracking-widest ${label.tone}`}>
                  {label.title}
                </span>
                <span className="text-slate-600 text-xs">· {items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((t) => <TaskItem key={t.id} task={t} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
